import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { searchComuni } from "../lib/comuni";
import AutocompleteInput from "./AutocompleteInput";
import SegnalatoreField from "./SegnalatoreField";
import AddressAutocomplete from "./AddressAutocomplete";
import DateInput from "./DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { computeBareme as computeBarem } from "../lib/bareme";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Check, Sparkles, MapPin, Search } from "lucide-react";
import SalvaDinamicaDialog from "./SalvaDinamicaDialog";
import { toast } from "sonner";
import { fetchTipologie, asSel, DEFAULT_TIPOLOGIE_SINISTRO } from "../lib/tipologieSinistro";
import { useNonAderenti, NON_ADERENTE_MSG } from "../lib/convenzione";

// Allineati alle opzioni del wizard Nuova Pratica (altrimenti i valori salvati non combaciano e appaiono vuoti).
const PROCEDURA_CARD = ["CARD", "DOPPIA FIRMA", "NO CARD", "NON APPLICABILE"];
const LESIONI = ["NESSUNA", "LIEVI", "GRAVI", "GRAVISSIME", "DECESSO"];
const VEICOLO_TIPI = [
  "veicolo non a motore",
  "autoveicolo",
  "ciclomotore/motoveicolo/macchina operatrice",
  "ciclomotore/motoveicolo - 'targhino'",
  "macchina agricola/rimorchio/operatrice non targata",
];
const AUTORITA_TIPI = ["Polizia di Stato", "Carabinieri", "Polizia Locale", "Guardia di Finanza", "Altra"];
const DOCS = ["MANDATO", "PRIVACY", "DOCUMENTO", "LIBRETTO", "CAI", "CERTIFICATO MEDICO"];
const DOC_CESSIONE = "CESSIONE DEL CREDITO";
function _hasCedente(p) {
  if (!p) return false;
  const roles = [p.assistito_ruolo, p.assistito_scheda && p.assistito_scheda.ruolo, ...((p.assistiti || []).map((a) => a && a.ruolo))].filter(Boolean).map((r) => String(r).toLowerCase());
  return roles.some((r) => r.includes("cedente"));
}
const BAREM = [
  "0 -",
  "1 - in sosta/in fermata",
  "2 - ripartiva da una sosta/apriva una portiera",
  "3 - stava parcheggiando",
  "4 - usciva da un parcheggio/da un luogo privato/da una strada vicinale",
  "5 - entrava in un parcheggio/in un luogo privato/in una strada vicinale",
  "6 - si immetteva in una piazza a senso rotatorio",
  "7 - circolava in una piazza a senso rotatorio",
  "8 - tamponava procedendo nello stesso senso e nella stessa fila",
  "9 - procedeva nello stesso senso ma in una fila diversa",
  "10 - cambiava fila",
  "11 - sorpassava",
  "12 - girava a destra",
  "13 - girava a sinistra",
  "14 - retrocedeva",
  "15 - invadeva la sede stradale riservata alla circolazione in senso inverso",
  "16 - proveniva da destra",
  "17 - non osservava il segnale di precedenza o di semaforo rosso",
];

// Calcola la responsabilità risultante dalla combinazione dei due casi Barem (tabella ufficiale in lib/bareme.js).

const SINISTRO_KEYS = [
  "numero_pratica", "tipo", "utente", "segnalatore", "agenzia", "data_apertura", "interruzione_prescrizione",
  "procedura_card", "lesioni", "data_sinistro", "ora_sinistro", "luogo_provincia", "luogo_sinistro",
  "indirizzo_sinistro", "sinistro_lat", "sinistro_lon", "veicoli_coinvolti", "autorita_intervenute",
  "autorita_tipo", "autorita_di", "autorita_verbale_n", "autorita_verbale_data",
  "barem_cliente", "barem_controparte", "responso_dpr", "descrizione_sinistro",
];

const LBL = "text-slate-300 text-xs";
const INPUT_CLS = "mt-1 h-9 bg-slate-900/60 border-slate-700";

export default function DatiSinistroDialog({ open, onOpenChange, pratica, onSaved }) {
  const [form, setForm] = useState({});
  const [tipiSel, setTipiSel] = useState(asSel(DEFAULT_TIPOLOGIE_SINISTRO));
  useEffect(() => { fetchTipologie().then((l) => setTipiSel(asSel(l))); }, []);
  const nonAderente = useNonAderenti();
  const cpNA = nonAderente(pratica?.controparti?.[0]?.compagnia || pratica?.controparte_compagnia || "");
  const [saving, setSaving] = useState(false);
  const [operators, setOperators] = useState([]);
  const [dinamiche, setDinamiche] = useState({});
  const [dinOpen, setDinOpen] = useState(false);
  const [dinFilter, setDinFilter] = useState("");
  const [salvaDinOpen, setSalvaDinOpen] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [veicoliErr, setVeicoliErr] = useState(false);

  useEffect(() => {
    if (!open || !pratica) return;
    setVeicoliErr(false);
    const f = {};
    SINISTRO_KEYS.forEach((k) => { f[k] = pratica[k] ?? ""; });
    const vc = Number(pratica.veicoli_coinvolti) || (pratica.veicoli || []).length || 2;
    f.veicoli_coinvolti = vc;
    const arr = [...(pratica.veicoli || [])];
    while (arr.length < vc) arr.push({ tipo: "", assicurato_ita: true });
    arr.length = vc;
    f.veicoli = arr;
    f.documentazione = Array.isArray(pratica.documentazione) ? [...pratica.documentazione] : [];
    if (_hasCedente(pratica) && !f.documentazione.includes(DOC_CESSIONE)) f.documentazione.push(DOC_CESSIONE);
    setForm(f);
    Promise.all([
      api.get("/studio/operators").catch(() => ({ data: [] })),
      api.get("/studio/dinamiche").catch(() => ({ data: {} })),
    ]).then(([o, d]) => { setOperators(o.data || []); setDinamiche(d.data || {}); });
  }, [open, pratica]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setVeicoli = (n) => {
    const num = Math.max(0, Math.min(10, Number(n) || 0));
    setForm((f) => { const a = [...(f.veicoli || [])]; while (a.length < num) a.push({ tipo: "", assicurato_ita: true }); a.length = num; return { ...f, veicoli_coinvolti: num, veicoli: a }; });
  };
  const patchVeicolo = (i, patch) => setForm((f) => ({ ...f, veicoli: (f.veicoli || []).map((v, idx) => (idx === i ? { ...v, ...patch } : v)) }));
  const toggleDoc = (d) => setForm((f) => { const s = new Set(f.documentazione || []); s.has(d) ? s.delete(d) : s.add(d); return { ...f, documentazione: [...s] }; });
  const mapsUrl = () => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([form.indirizzo_sinistro, form.luogo_sinistro, form.luogo_provincia].filter(Boolean).join(", "))}`;

  const save = async () => {
    if ((form.veicoli || []).some((v) => !(v && v.tipo && String(v.tipo).trim()))) {
      setVeicoliErr(true);
      toast.error("Selezionare la tipologia del veicolo per ogni veicolo");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, veicoli_coinvolti: Number(form.veicoli_coinvolti) || 0, barem_responsabilita: (computeBarem(form.barem_cliente, form.barem_controparte) || {}).text || "" };
      const { data } = await api.patch(`/studio/pratiche/${pratica.id}`, payload);
      toast.success("Dati sinistro aggiornati");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const generaAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/studio/dinamica-ai", { tipo: form.tipo, contesto: form.descrizione_sinistro });
      setForm((f) => ({ ...f, descrizione_sinistro: data.text, ...(data.responso ? { responso_dpr: data.responso } : {}) }));
      setAiGenerated(true);
      toast.success(data.responso ? `Dinamica generata · Responso: ${data.responso}` : "Dinamica generata con AI");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const ro = (label, val, tid) => (
    <div><Label className={LBL}>{label}</Label><div data-testid={tid} className="mt-1 h-9 flex items-center px-3 rounded-md bg-slate-900/40 border border-slate-800 text-slate-300 text-sm truncate">{val || "—"}</div></div>
  );
  const dateField = (k, label) => (
    <div><Label className={LBL}>{label}</Label><div className="mt-1"><DateInput value={form[k]} onChange={upd(k)} testId={`ds-${k}`} /></div></div>
  );
  const sel = (k, label, options, placeholder = "Seleziona") => (
    <div>
      <Label className={LBL}>{label}</Label>
      <Select value={form[k] || ""} onValueChange={(v) => setForm((f) => ({ ...f, [k]: v === "__none__" ? "" : v }))}>
        <SelectTrigger data-testid={`ds-${k}`} className="mt-1 h-9 bg-slate-900/60 border-slate-700 text-slate-100 [&>svg]:opacity-100"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
          <SelectItem value="__none__">—</SelectItem>
          {options.map((o) => <SelectItem key={String(o.v ?? o)} value={String(o.v ?? o)} disabled={!!o.disabled}>{o.l ?? o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const utenteVal = pratica?.utente || pratica?.utente_id || "";
  const caricataVal = pratica?.created_by_name || pratica?.creato_da || pratica?.utente || "";

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="bg-[#121824] border-slate-700 text-slate-100 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="dati-sinistro-dialog">
        <DialogHeader><DialogTitle className="font-head">Modifica dati sinistro {form.tipo ? `· ${form.tipo}` : ""}</DialogTitle></DialogHeader>

        <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">
          {/* COLONNA SINISTRA */}
          <div className="space-y-3">
            {ro("Codice", form.numero_pratica, "ds-numero_pratica")}
            {ro("Pratica caricata da", caricataVal, "ds-caricata-da")}
            {dateField("data_apertura", "Apertura")}
            {dateField("interruzione_prescrizione", "Interruzione Prescrizione")}
            <div>
              <Label className={LBL}>Agenzia</Label>
              <Input value={form.agenzia || ""} onChange={set("agenzia")} data-testid="ds-agenzia" autoComplete="off" placeholder="-- NESSUNA AGENZIA --" className={INPUT_CLS} />
            </div>
            <div>
              <Label className={LBL}>Segnalatore</Label>
              <SegnalatoreField value={form.segnalatore || ""} onChange={upd("segnalatore")} testId="ds-segnalatore" placeholder="-- NESSUN SEGNALATORE --" />
            </div>
            {ro("Utente", utenteVal, "ds-utente")}
            {sel("tipo", "Tipo sinistro / Pratica", cpNA ? tipiSel.map((o) => (String(o.v ?? o).toUpperCase() === "RD" ? { v: o.v ?? o, l: o.l ?? o, disabled: true } : o)) : tipiSel)}
            {cpNA && <p className="-mt-2 text-[11px] text-amber-400" data-testid="ds-tipo-non-aderente">{NON_ADERENTE_MSG}</p>}
            {sel("procedura_card", "Procedura Card / Doppia Firma", PROCEDURA_CARD)}
            {sel("lesioni", "Lesioni", LESIONI)}
            {dateField("data_sinistro", "Data Sinistro")}
            <div><Label className={LBL}>Ora Sinistro</Label><Input type="time" value={form.ora_sinistro || ""} onChange={set("ora_sinistro")} data-testid="ds-ora_sinistro" autoComplete="off" className={INPUT_CLS} /></div>
          </div>

          {/* COLONNA DESTRA */}
          <div className="space-y-3">
            <div><Label className={LBL}>Provincia</Label><Input value={form.luogo_provincia || ""} readOnly data-testid="ds-luogo_provincia" placeholder="—" className={`${INPUT_CLS} font-mono bg-slate-800/60 cursor-not-allowed`} title="Si popola dalla città" /></div>
            <div>
              <Label className={LBL}>Città</Label>
              <div className="mt-1"><AutocompleteInput source="comuni" value={form.luogo_sinistro || ""} onChange={upd("luogo_sinistro")} onSelectItem={(it) => setForm((f) => ({ ...f, luogo_provincia: it.provincia || it.prov || f.luogo_provincia }))} placeholder="Digita il comune…" testId="ds-luogo_sinistro" /></div>
            </div>
            <div>
              <Label className={LBL}>Indirizzo</Label>
              <div className="mt-1 flex gap-2">
                <AddressAutocomplete value={form.indirizzo_sinistro} onChange={upd("indirizzo_sinistro")} onPick={(pk) => setForm((f) => ({ ...f, indirizzo_sinistro: pk.indirizzo, luogo_sinistro: pk.comune || f.luogo_sinistro, luogo_provincia: pk.provincia || f.luogo_provincia, sinistro_lat: String(pk.lat), sinistro_lon: String(pk.lon) }))} city={form.luogo_sinistro} prov={form.luogo_provincia} testId="ds-indirizzo_sinistro" />
                <a href={mapsUrl()} target="_blank" rel="noreferrer" data-testid="ds-maps" title="Apri in Google Maps" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-amber-400 hover:bg-slate-700"><MapPin className="h-4 w-4" /></a>
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-teal-300 font-mono">N° Veicoli Coinvolti nell'Urto</span>
                <Input type="number" min={0} value={form.veicoli_coinvolti} onChange={(e) => setVeicoli(e.target.value)} data-testid="ds-n-veicoli" className="h-8 w-20 bg-slate-900/60 border-slate-700 font-mono" />
              </div>
              <div className="mt-3 space-y-2 max-w-xl">
                {(form.veicoli || []).map((v, i) => {
                  const showErr = veicoliErr && !(v && v.tipo && String(v.tipo).trim());
                  return (
                    <div key={i} data-testid={`ds-veicolo-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-mono text-slate-400 w-16 shrink-0">VEICOLO {i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <Select value={v.tipo || ""} onValueChange={(val) => patchVeicolo(i, { tipo: val })}>
                            <SelectTrigger data-testid={`ds-veicolo-${i}-tipo`} className={`h-9 w-full bg-slate-900/60 text-slate-100 [&>svg]:opacity-100 ${showErr ? "border-red-500/70" : "border-slate-700"}`}><SelectValue placeholder="SELEZIONARE VEICOLO" /></SelectTrigger>
                            <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">{VEICOLO_TIPI.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs text-slate-300 whitespace-nowrap shrink-0"><Checkbox checked={!!v.assicurato_ita} onCheckedChange={(c) => patchVeicolo(i, { assicurato_ita: !!c })} data-testid={`ds-veicolo-${i}-assicurato`} /> Assicurato</label>
                      </div>
                      {showErr && <p className="text-[11px] text-red-400 mt-1 ml-[76px]" data-testid={`ds-veicolo-${i}-error`}>Selezionare la tipologia del veicolo</p>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 p-3">
              <Label className={LBL}>Autorità Intervenute</Label>
              <RadioGroup value={form.autorita_intervenute || ""} onValueChange={(val) => setForm((f) => (val === "SI" ? { ...f, autorita_intervenute: val } : { ...f, autorita_intervenute: val, autorita_tipo: "", autorita_di: "", autorita_verbale_n: "", autorita_verbale_data: "" }))} className="flex gap-6 mt-2" data-testid="ds-autorita">
                {["SI", "NO", "NON SO"].map((o) => <label key={o} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><RadioGroupItem value={o} data-testid={`ds-autorita-${o.replace(/\s/g, "")}`} /> {o}</label>)}
              </RadioGroup>
              {form.autorita_intervenute === "SI" && (
                <div className="grid grid-cols-2 gap-2 mt-3" data-testid="ds-autorita-dettagli">
                  {sel("autorita_tipo", "Tipo", AUTORITA_TIPI)}
                  <div><Label className={LBL}>Di</Label><Input value={form.autorita_di || ""} onChange={set("autorita_di")} data-testid="ds-autorita-di" className={INPUT_CLS} placeholder="Es. Palermo" /></div>
                  <div><Label className={LBL}>N. Verbale</Label><Input value={form.autorita_verbale_n || ""} onChange={set("autorita_verbale_n")} data-testid="ds-autorita-verbale-n" className={`${INPUT_CLS} font-mono`} /></div>
                  {dateField("autorita_verbale_data", "Data Verbale")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BAREM RESPONSABILITÀ */}
        <div className="mt-4 rounded-lg border border-slate-700 p-3">
          <div className="text-[11px] uppercase tracking-wider text-teal-300 font-mono mb-2">Barem Responsabilità</div>
          <div className="grid md:grid-cols-2 gap-3">
            {sel("barem_cliente", "Veicolo Cliente", BAREM, "-- seleziona caso --")}
            {sel("barem_controparte", "Veicolo Controparte", BAREM, "-- seleziona caso --")}
          </div>
          {(() => {
            const b = computeBarem(form.barem_cliente, form.barem_controparte);
            if (!b) return null;
            return (
              <div data-testid="ds-barem-responsabilita" className="mt-3 rounded-md px-4 py-3 text-center font-bold uppercase tracking-wide text-white" style={{ backgroundColor: b.bg }}>
                {b.text}
              </div>
            );
          })()}
          {(form.barem_cliente || form.barem_controparte || form.responso_dpr) && (
            <div className="mt-3 rounded-md border border-amber-500/50 bg-amber-400/90 px-3 py-2 text-sm font-semibold text-amber-950" data-testid="ds-barem-banner">
              {form.responso_dpr || [form.barem_cliente, form.barem_controparte].filter(Boolean).join("  /  ")}
            </div>
          )}
        </div>

        {/* DESCRIZIONE SINISTRO */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <Label className={LBL}>Descrizione Sinistro</Label>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setDinOpen((o) => !o)} data-testid="ds-scegli-dinamica" className="h-7 border-slate-700 text-slate-200 hover:bg-slate-800">Scegli dinamica</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setSalvaDinOpen(true)} data-testid="ds-salva-dinamica" className="h-7 border-slate-700 text-slate-200 hover:bg-slate-800">Salva dinamica</Button>
              <Button type="button" size="sm" onClick={generaAI} disabled={aiLoading} data-testid="ds-ai" className="h-7 bg-violet-600 hover:bg-violet-700 text-white">{aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> AI</>}</Button>
            </div>
          </div>
          {dinOpen && (
            <div className="mt-1.5 rounded-md border border-slate-700 bg-slate-900/40 p-2 space-y-1.5" data-testid="ds-dinamiche-list">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                <Input value={dinFilter} onChange={(e) => setDinFilter(e.target.value)} data-testid="ds-dinamiche-search" noUppercase
                  placeholder="Cerca nelle dinamiche (es. veicolo in sosta)…" className="h-8 pl-7 bg-slate-900/60 border-slate-700 text-slate-100 text-xs" />
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {(() => {
                  const all = dinamiche[form.tipo] || [];
                  const q = dinFilter.trim().toLowerCase();
                  const list = q ? all.filter((t) => String(t || "").toLowerCase().includes(q)) : all;
                  if (all.length === 0) return <p className="text-xs text-slate-500 px-1">Nessuna dinamica per {form.tipo || "questo tipo"}.</p>;
                  if (list.length === 0) return <p className="text-xs text-slate-500 px-1" data-testid="ds-dinamiche-empty">Nessuna dinamica corrisponde a "{dinFilter.trim()}".</p>;
                  return list.map((t, i) => (
                    <button key={i} type="button" onClick={() => { setForm((f) => ({ ...f, descrizione_sinistro: t })); setDinOpen(false); setDinFilter(""); }} className="w-full text-left p-2 rounded text-xs text-slate-200 hover:bg-slate-800/60">{t}</button>
                  ));
                })()}
              </div>
            </div>
          )}
          <Textarea value={form.descrizione_sinistro || ""} onChange={set("descrizione_sinistro")} rows={5} data-testid="ds-descrizione_sinistro" autoComplete="off" className="mt-1.5 bg-slate-900/60 border-slate-700" />
        </div>

        {/* DOCUMENTAZIONE NECESSARIA */}
        <div className="mt-4">
          <Label className={LBL}>Documentazione Necessaria</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="ds-documentazione">
            {(_hasCedente(pratica) ? [...DOCS, DOC_CESSIONE] : DOCS).map((d) => {
              const on = (form.documentazione || []).includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleDoc(d)} data-testid={`ds-doc-${d.replace(/\s/g, "-")}`}
                  className={`text-xs font-mono px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-teal-500/20 border-teal-500/50 text-teal-200" : "bg-slate-900/40 border-slate-700 text-slate-400 hover:bg-slate-800/60"}`}>
                  {on ? "✓ " : ""}{d}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="ds-save" className="bg-slate-800 hover:bg-slate-700 text-white font-semibold border border-slate-600">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> AGGIORNA</>}
          </Button>
        </div>
      </DialogContent>
      <SalvaDinamicaDialog open={salvaDinOpen} onOpenChange={setSalvaDinOpen} testo={form.descrizione_sinistro || ""} tipo={form.tipo || ""} onSaved={() => api.get("/studio/dinamiche").then(({ data }) => setDinamiche(data || {})).catch(() => {})} />
    </Dialog>
  );
}
